import { env } from 'next-runtime-env';

/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 authorization code flow.
 * Implements RFC 7636 with S256 challenge method.
 *
 * Client-side: generate verifier + challenge, store verifier in cookie
 * Server-side: read verifier from cookie, include in token exchange when enabled
 */

/** Cookie used to carry the PKCE code_verifier from /authorize to /api/auth/callback/okta. */
export const OKTA_PKCE_VERIFIER_COOKIE = 'okta-pkce-verifier';

/**
 * When `NEXT_PUBLIC_OKTA_USE_PKCE` is truthy (e.g. `true`, `1`, `yes`, `on`), the login flow
 * sends `code_challenge` / `code_challenge_method=S256` and the callback sends `code_verifier`.
 */
export function isOktaPkceEnabled(): boolean {
  const v = String(env('NEXT_PUBLIC_OKTA_USE_PKCE') ?? '')
    .trim()
    .toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** RFC 7636: 43–128 char code_verifier from high-entropy octets. */
export async function createPkcePair(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const codeVerifier = base64UrlEncode(verifierBytes);

  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const codeChallenge = base64UrlEncode(new Uint8Array(hashBuffer));

  return { codeVerifier, codeChallenge };
}

/** Store verifier for the OAuth redirect round-trip (browser only). */
export function storePkceVerifierCookie(codeVerifier: string): void {
  document.cookie = `${OKTA_PKCE_VERIFIER_COOKIE}=${encodeURIComponent(codeVerifier)}; path=/; max-age=600; SameSite=Lax; Secure`;
}

export function getPkceVerifierFromCookies(cookies: Partial<Record<string, string>>): string | undefined {
  return cookies[OKTA_PKCE_VERIFIER_COOKIE];
}

export function clearPkceVerifierCookie(): string {
  return `${OKTA_PKCE_VERIFIER_COOKIE}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}
